import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { daDate } from "@/lib/date";
import { MovedBadge } from "@/components/MovedBadge";
import { Button } from "@/components/ui/button";
import { useServerView, activeFilters, ListActions, ListPrint } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { ProjectShareDialog } from "@/components/projects/ProjectShareDialog";
import { Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PlusSquare, FolderKanban, Clock, CheckCircle2, CircleDashed, X, ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, ChevronDown, Trash2, Star, History } from "lucide-react";

const statusLabel = { ikke_igangsat: "Ikke igangsat", aktiv: "Aktiv", paused: "Pauset", afsluttet: "Afsluttet" };
const statusColor = {
  ikke_igangsat: "bg-stone-50 text-stone-500 border-stone-100",
  aktiv: "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
  afsluttet: "bg-stone-50 text-stone-500 border-stone-100",
  paused: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
};
const statusDot = { ikke_igangsat: "#D6D3D1", aktiv: "#7E856C", paused: "#A08979", afsluttet: "#A8A29E" };
const statusOrder = { ikke_igangsat: 0, aktiv: 1, paused: 2, afsluttet: 3 };
const stepColor = {
  "Ikke startet": "bg-stone-50 text-stone-500 border-stone-100",
  "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};

const Pill = ({ className = "", children, ...rest }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${className}`} {...rest}>{children}</span>
);

function Stat({ icon: Icon, label, value, accent, iconColor, onClick, active }) {
  const clickable = !!onClick;
  return (
    <div
      className={`pcard pcard-hover p-6 flex items-center gap-4 ${clickable ? "cursor-pointer" : ""} ${active ? "ring-2 ring-[#A83232] ring-offset-2 ring-offset-[#FBFAF8]" : ""}`}
      data-testid={`stat-${label}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}>
      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${accent}`}><Icon className="h-[22px] w-[22px]" strokeWidth={1.75} style={{ color: iconColor }} /></div>
      <div>
        <p className="text-3xl font-semibold tracking-tight leading-none text-[#1A1919]">{value}</p>
        <p className="label-caps mt-1.5">{label}</p>
      </div>
    </div>
  );
}

const sorters = {
  name: (a, b) => (a.name || "").localeCompare(b.name || "", "da"),
  kunde: (a, b) => (a.customer_name || "").localeCompare(b.customer_name || "", "da"),
  pm: (a, b) => (a.project_manager_name || "").localeCompare(b.project_manager_name || "", "da"),
  status: (a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9),
  current: (a, b) => (a.current_step_name || "").localeCompare(b.current_step_name || "", "da"),
  curresp: (a, b) => (a.current_step_responsible_name || "").localeCompare(b.current_step_responsible_name || "", "da"),
  start: (a, b) => String(a.start_date || "").localeCompare(String(b.start_date || "")),
  end: (a, b) => String(a.expected_end_date || "").localeCompare(String(b.expected_end_date || "")),
  progress: (a, b) => a.progress - b.progress,
  hours: (a, b) => (a.total_hours || 0) - (b.total_hours || 0),
};

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const sv = useServerView("/project-view/list", { exportPath: "/project-view/list/export.xlsx" });
  const projects = sv.rows;
  const total = sv.data?.total ?? 0;
  const loading = sv.data === null;
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const canShare = user?.role === "admin" || (user?.role === "projektleder" && (user?.departments || []).includes("salg"));
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const loadSaved = () => api.get("/saved-filters").then((r) => setSavedFilters(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const getArr = sv.getArr;
  const pmSel = getArr("projektleder");
  const respSel = getArr("ansvarlig");
  const statusSel = getArr("status");
  const kundeSel = getArr("kunde");
  const trinSel = getArr("trin");
  const match = sv.match;
  const sort = params.get("sort") || "";
  const dir = params.get("dir") || "asc";
  const uid = user?.id;
  const visning = params.get("visning");
  const pmMe = params.get("projektansvarlig") === "me";
  const myAnsvar = (p) => {
    const parts = [];
    if (p.project_manager_id === uid) parts.push("Projektansvarlig");
    const names = (p.steps || []).filter((s) => s.selected && (s.responsible_user_ids && s.responsible_user_ids.length ? s.responsible_user_ids : (s.responsible_user_id ? [s.responsible_user_id] : [])).includes(uid)).map((s) => s.name);
    if (names.length) parts.push(names.join(", "));
    return parts.join(" · ") || "—";
  };

  const currentView = visning === "mit-ansvar" ? "mit-ansvar" : "alle";
  const defaultView = user?.default_project_view || "alle";
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (appliedDefault.current || !user) return;
    appliedDefault.current = true;
    const hasView = params.get("visning") || params.get("projektansvarlig");
    if (!hasView && user.default_project_view === "mit-ansvar") setParam({ visning: "mit-ansvar" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveDefault = async () => {
    try {
      const r = await api.patch("/auth/me/preferences", { default_project_view: currentView });
      updateUser({ default_project_view: r.data.default_project_view });
      toast.success(currentView === "mit-ansvar" ? "Standard sat til Mit ansvar" : "Standard sat til Alle projekter");
    } catch (e) { toast.error("Kunne ikke gemme standardvisning"); }
  };

  const setParam = sv.setParam;
  const toggleStatus = (val) => setParam({ status: statusSel.includes(val) ? statusSel.filter((x) => x !== val) : [...statusSel, val] });
  const resetFilters = sv.reset;
  const toggleSort = (col) => setParam(sort === col ? { sort: col, dir: dir === "asc" ? "desc" : "asc" } : { sort: col, dir: "asc" });
  const applySaved = (sf) => setParams(new URLSearchParams(sf.query || ""));
  const saveCurrent = async () => {
    if (!saveName.trim()) return;
    try { await api.post("/saved-filters", { name: saveName.trim(), query: params.toString() }); toast.success("Filter gemt"); setSaveOpen(false); setSaveName(""); loadSaved(); }
    catch (e) { toast.error("Kunne ikke gemme filter"); }
  };
  const deleteSaved = async (id, e) => { e?.stopPropagation?.(); try { await api.delete(`/saved-filters/${id}`); toast.success("Filter slettet"); loadSaved(); } catch (_) {} };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      sv.reload();
      toast.success(`Projektet "${deleteTarget.name}" blev slettet`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette projektet");
    } finally { setDeleting(false); }
  };

  const filtered = useMemo(() => (sorters[sort] ? [...projects].sort((a, b) => sorters[sort](a, b) * (dir === "asc" ? 1 : -1)) : projects), [projects, sort, dir]);
  const counts = sv.data?.counts || { ikke_igangsat: 0, aktiv: 0, afsluttet: 0, hours: 0 };
  const active = { length: counts.aktiv };
  const notStarted = counts.ikke_igangsat;
  const completed = counts.afsluttet;
  const totalHours = counts.hours;

  const anyFilter = [pmSel, statusSel, respSel, kundeSel, trinSel, getArr("fremdrift"), getArr("timer")].some((a) => a.length > 0) || visning === "mit-ansvar" || visning === "forsinket" || pmMe || !!sv.search || sv.period.from || sv.period.to;
  const FLABELS = { kunde: "Kunde", projektleder: "Projektansvarlig", status: "Status", trin: "Aktuelt trin", ansvarlig: "Ansvarlig for aktuelt trin", fremdrift: "Fremdrift", timer: "Timer" };
  const pfilters = [...activeFilters(sv, FLABELS)];
  if (visning || pmMe) pfilters.unshift(["Visning", visning === "mit-ansvar" ? "Mit ansvar" : visning === "forsinket" ? "Forsinkede – mit ansvar" : "Jeg er projektansvarlig"]);

  const exportExcel = async () => {
    try {
      const res = await api.get(`/projects/export/excel?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "projektoversigt.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const SortIcon = ({ col }) => sort !== col
    ? <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
    : dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  const SortH = ({ col, span, align = "left", children }) => (
    <button onClick={() => toggleSort(col)} data-testid={`sort-${col}`} className={`${span} flex items-center gap-1.5 label-caps hover:text-stone-700 [transition-property:color] duration-200 ${align === "right" ? "justify-end" : ""}`}>{children} <SortIcon col={col} /></button>
  );

  const CurrentStep = ({ p }) => {
    if (p.current_step_mode !== "step") return <span className="text-muted-foreground text-xs">{p.current_step_name}</span>;
    const others = (p.other_in_progress || []).map((o) => `${o.name} (${o.responsible || "Ikke tildelt"})`).join("\n");
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Pill className={stepColor[p.current_step_status] || ""}>{p.current_step_name}</Pill>
        {p.current_step_delayed && <Pill className="bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]">Forsinket</Pill>}
        {p.current_step_extra_in_progress > 0 && <span className="text-xs text-muted-foreground cursor-help" title={others}>+{p.current_step_extra_in_progress} andet trin i gang</span>}
      </div>
    );
  };

  return (
    <div className="rise-in">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="label-caps">Velkommen, {user?.name?.split(" ")[0]}</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight mt-2 text-[#1A1919]">Projektoversigt</h1>
        </div>
        <div className="flex gap-2.5">
          <ListActions onExport={sv.exportXlsx} more={[...(canShare ? [{ label: "Del med kunde", onSelect: () => setShareOpen(true), testid: "proj-share-btn-mobile", mobileOnly: true }] : [])]}
            primary={(user?.role === "admin" || (user?.role === "projektleder" && (user?.departments || []).includes("salg"))) && (
            <Button data-testid="create-project-btn" onClick={() => nav("/opret-projekt")} className="rounded-xl font-medium"><PlusSquare className="h-4 w-4 mr-2" strokeWidth={1.75} /> Opret nyt projekt</Button>
          )}>{canShare && <Button variant="outline" size="sm" className="rounded-xl border-[#EBE8E3] bg-white h-9 hidden md:inline-flex" onClick={() => setShareOpen(true)} data-testid="proj-share-btn"><Share2 className="h-4 w-4 mr-1.5" />Del med kunde</Button>}</ListActions>
        </div>
      </div>
      {canShare && <ProjectShareDialog open={shareOpen} onClose={() => setShareOpen(false)} customers={(sv.data?.options?.kunde || []).map((o) => o.value)} defaultParty={kundeSel.length === 1 ? kundeSel[0] : ""} />}

      {(visning === "mit-ansvar" || visning === "forsinket" || pmMe) && (
        <div className="flex items-center gap-2 mb-6" data-testid="personal-filter-banner">
          <span className="inline-flex items-center gap-2 bg-[#F1DEDA] text-[#A83232] border border-[#E9C9C3] rounded-full pl-3.5 pr-2 py-1.5 text-sm font-medium" data-testid="personal-filter-chip">
            {visning === "mit-ansvar" ? "Mit ansvar" : visning === "forsinket" ? "Forsinkede – mit ansvar" : "Jeg er projektansvarlig"}
            <button onClick={() => setParam({ visning: null, projektansvarlig: null })} data-testid="clear-personal-filter" className="p-0.5 hover:bg-[#E9C9C3] rounded-full"><X className="h-3.5 w-3.5" /></button>
          </span>
          <span className="text-sm text-muted-foreground">{filtered.length} projekter</span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <h2 className="label-caps">Nøgletal</h2>
        {anyFilter && <span className="text-xs font-medium text-[#A83232] bg-[#F1DEDA] border border-[#E9C9C3] rounded-full px-2 py-0.5" data-testid="kpi-filtered-note">filtreret</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Stat icon={CircleDashed} label="Ikke igangsatte" value={notStarted} accent="bg-stone-100" iconColor="#78716C" active={statusSel.includes("ikke_igangsat")} onClick={() => toggleStatus("ikke_igangsat")} />
        <Stat icon={FolderKanban} label="Aktive" value={active.length} accent="bg-[#F1DEDA]" iconColor="#A83232" active={statusSel.includes("aktiv")} onClick={() => toggleStatus("aktiv")} />
        <Stat icon={CheckCircle2} label="Afsluttet" value={completed} accent="bg-[#E7EADF]" iconColor="#7E856C" active={statusSel.includes("afsluttet")} onClick={() => toggleStatus("afsluttet")} />
        <Stat icon={Clock} label="Total timeforbrug" value={`${totalHours} t`} accent="bg-stone-100" iconColor="#57534E" />
      </div>

      <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick placeholder="Søg projekt, kunde, ansvarlig, trin…" shown={filtered.length} total={total} onReset={resetFilters} leadActive={!!visning || pmMe} match={match} setMatch={sv.setMatch}
        filters={[sv.mkFilter("kunde", "Kunde"), sv.mkFilter("projektleder", "Projektansvarlig"), sv.mkFilter("status", "Status"), sv.mkFilter("trin", "Aktuelt trin"), sv.mkFilter("ansvarlig", "Ansvarlig for aktuelt trin"), sv.mkFilter("fremdrift", "Fremdrift"), sv.mkFilter("timer", "Timer")]} period={sv.period}
        saved={savedFilters} onApplySaved={applySaved} onDeleteSaved={deleteSaved} onSaveCurrent={() => setSaveOpen(true)}
        lead={<>
          <div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center" data-testid="quick-view-toggle">
            <button type="button" data-testid="quick-all" onClick={() => setParam({ visning: null, projektansvarlig: null })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${!visning && !pmMe ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Alle projekter</button>
            <button type="button" data-testid="quick-mit-ansvar" onClick={() => setParam({ visning: "mit-ansvar", projektansvarlig: null })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${visning === "mit-ansvar" ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Mit ansvar</button>
          </div>
          {!pmMe && visning !== "forsinket" && (defaultView === currentView
            ? <span className="inline-flex items-center gap-1 text-xs text-[#A83232] font-medium" data-testid="default-view-active"><Star className="h-3.5 w-3.5 fill-[#A83232]" /> Din standard</span>
            : <button type="button" onClick={saveDefault} data-testid="set-default-view" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#A83232] [transition-property:color] duration-200"><Star className="h-3.5 w-3.5" /> Sæt som standard</button>
          )}
        </>} />
      <ListPrint kicker="BSM A/S · Salg" title="Projektoversigt" noun="projekter" filters={pfilters}
        kpis={[{ label: "Projekter i udvalget", value: filtered.length }, { label: "Aktive", value: counts.aktiv, dark: true }, { label: "Ikke igangsatte", value: counts.ikke_igangsat }, { label: "Timer i udvalget", value: `${counts.hours} t` }]}
        summary={<PSummary>{pl(filtered.length, "projekt", "projekter")} i udvalget ud af {total} i alt – {pl(counts.aktiv, "aktivt", "aktive")}, {counts.ikke_igangsat} ikke igangsat og {counts.afsluttet} afsluttet. Samlet timeforbrug {counts.hours} t.</PSummary>}
        cols={[{ label: "Projekt", key: "n2" }, { label: "Projektansvarlig", key: "pm2", w: "33mm" }, { label: "Status", key: "st", w: "14mm", cls: "nowrap" }, { label: "Aktuelt trin", key: "cs2", w: "35mm" }, { label: "Deadline", key: "dl", w: "22mm", cls: "nowrap" }, { label: "Fremdrift", key: "pr", w: "17mm", cls: "nowrap num" }, { label: "Timer", key: "hrs", w: "12mm", cls: "nowrap num" }]}
        rows={filtered.map((p) => ({ ...p, n2: <span><span className="pd-name">{p.name}</span><span className="pd-sub2">{p.customer_name || "—"}</span></span>, pm2: p.project_manager_name || "—", st: statusLabel[p.status] || p.status, cs2: p.current_step_name || "—", dl: p.expected_end_date ? daDate(p.expected_end_date) : "—", pr: `${p.progress} %`, hrs: p.total_hours ? `${p.total_hours} t` : "—" }))} />

      {loading ? (
        <div className="pcard p-8 text-muted-foreground text-sm">Indlæser projekter…</div>
      ) : total === 0 ? (
        <div className="pcard p-16 text-center"><FolderKanban className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} /><p className="mt-4 text-muted-foreground">Ingen projekter endnu.</p>
          {(user?.role === "admin" || (user?.role === "projektleder" && (user?.departments || []).includes("salg"))) && <Button variant="outline" className="mt-5 rounded-xl border-[#EBE8E3]" onClick={() => nav("/opret-projekt")}>Opret dit første projekt</Button>}</div>
      ) : filtered.length === 0 ? (
        <div className="pcard p-16 text-center" data-testid="no-match-message"><FolderKanban className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} /><p className="mt-4 text-muted-foreground">Ingen projekter matcher de valgte filtre.</p>
          <Button variant="outline" className="mt-5 rounded-xl border-[#EBE8E3]" onClick={resetFilters}>Nulstil filtre</Button></div>
      ) : (
        <div className="space-y-2.5" data-testid="project-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 pb-1">
            <SortH col="name" span="col-span-3">Projekt</SortH>
            <SortH col="pm" span="col-span-2">Projektansvarlig</SortH>
            <SortH col="status" span="col-span-2">Status</SortH>
            <SortH col="current" span="col-span-2">Aktuelt trin</SortH>
            <SortH col="end" span="col-span-1">Deadline</SortH>
            <SortH col="hours" span="col-span-1" align="right">Timer</SortH>
            <div className="col-span-1 label-caps text-right">Handling</div>
          </div>
          {filtered.map((p) => {
            const isOpen = expanded.has(p.id);
            return (
              <div key={p.id} data-testid={`project-row-${p.id}`} className="pcard pcard-hover">
                <div data-testid={`project-row-body-${p.id}`} onClick={() => nav(`/projekt/${p.id}`)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); nav(`/projekt/${p.id}`); } }} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center p-4 md:px-5 md:py-4 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A83232]">
                  <div className="md:col-span-3 min-w-0">
                    <p className="font-semibold text-[#1A1919] truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{p.customer_name || "—"}</p>
                    {(visning === "mit-ansvar" || visning === "forsinket") && <p className="text-xs text-[#A83232] font-medium mt-1" data-testid={`ansvar-${p.id}`}>Dit ansvar: {myAnsvar(p)}</p>}
                  </div>
                  <div className="md:col-span-2 text-sm text-stone-600 truncate"><span className="md:hidden label-caps mr-2">Ansvarlig</span>{p.project_manager_name || "—"}</div>
                  <div className="md:col-span-2"><Pill className={statusColor[p.status] || ""} data-testid={`row-status-${p.id}`}><span className="w-1.5 h-1.5 rounded-full" style={{ background: statusDot[p.status] }} />{statusLabel[p.status] || p.status}</Pill></div>
                  <div className="md:col-span-2" data-testid={`row-current-${p.id}`}><CurrentStep p={p} /></div>
                  <div className="md:col-span-1 text-sm text-stone-600" data-testid={`row-end-${p.id}`}><span className="md:hidden label-caps mr-2">Deadline</span>{p.expected_end_date ? daDate(p.expected_end_date) : "Ikke fastlagt"}
                    <MovedBadge history={p.deadline_history} testid={`deadline-changed-${p.id}`} className="ml-1.5" />
                  </div>
                  <div className="md:col-span-1 text-sm font-semibold text-[#1A1919] md:text-right" data-testid={`row-hours-${p.id}`}><span className="md:hidden label-caps mr-2">Timer</span>{p.total_hours ? `${p.total_hours} t` : "—"}</div>
                  <div className="md:col-span-1 flex items-center gap-1 md:justify-end" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`expand-project-${p.id}`} aria-expanded={isOpen} onClick={() => toggleExpand(p.id)} title="Vis mere" className="p-1.5 rounded-lg text-muted-foreground hover:text-[#1A1919] hover:bg-[#F3F1EC] [transition-property:color,background-color] duration-200"><ChevronDown className={`h-4 w-4 [transition-property:transform] duration-200 ${isOpen ? "rotate-180" : ""}`} /></button>
                    {p.can_edit && (
                      <button data-testid={`delete-project-${p.id}`} onClick={() => setDeleteTarget(p)} title="Slet projekt" className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-[#F1DEDA] [transition-property:color,background-color] duration-200"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#EBE8E3] px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4" data-testid={`row-details-${p.id}`}>
                    <div><p className="label-caps mb-1.5">Fremdrift</p><div className="flex items-center gap-2.5"><div className="flex-1 h-1.5 rounded-full bg-[#ECEAE5] overflow-hidden max-w-[120px]"><div className="h-full rounded-full bg-[#A83232]" style={{ width: `${p.progress}%` }} /></div><span className="text-xs font-semibold text-stone-700">{p.progress}%</span></div></div>
                    <div><p className="label-caps mb-1.5">Startdato</p><p className="text-sm text-stone-600">{daDate(p.start_date)}</p></div>
                    <div><p className="label-caps mb-1.5">Ansvarlig for aktuelt trin</p><p className="text-sm text-stone-600">{p.current_step_mode === "step" ? (p.current_step_responsible_name || "Ikke tildelt") : "–"}</p></div>
                    <div className="flex items-end"><button data-testid={`open-project-${p.id}`} onClick={() => nav(`/projekt/${p.id}`)} className="inline-flex items-center text-primary font-semibold text-xs">Åbn projekt <ChevronRight className="h-4 w-4" /></button></div>
                    {p.deadline_history?.length > 0 && (
                      <div className="col-span-2 md:col-span-4 pt-1" data-testid={`deadline-history-block-${p.id}`}>
                        <p className="label-caps mb-1.5 flex items-center gap-1.5"><History className="h-3.5 w-3.5 text-[#A83232]" /> Ændret deadline</p>
                        <div className="rounded-xl border border-[#EBE8E3] divide-y divide-[#F3F1EC] overflow-hidden">
                          {[...p.deadline_history].reverse().map((h, i) => (
                            <div key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-sm" data-testid={`deadline-history-row-${p.id}-${i}`}>
                              <span className="font-medium text-[#1A1919]"><span className="text-stone-400 line-through mr-1.5">{daDate(h.from)}</span>→ {daDate(h.to)}</span>
                              <span className="text-stone-600">{h.reason || "Ingen årsag angivet"}</span>
                              <span className="text-xs text-stone-400 ml-auto">{h.by || "—"}{h.at ? ` · ${daDate(h.at)}` : ""}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-xl font-medium">Gem filteropsætning</DialogTitle>
            <DialogDescription>Giv genvejen et navn, fx "Mine forsinkede projekter". De aktive filtre og sortering gemmes.</DialogDescription></DialogHeader>
          <Input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Navn på filter" data-testid="save-filter-name" onKeyDown={(e) => e.key === "Enter" && saveCurrent()} className="rounded-xl" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setSaveOpen(false)}>Annullér</Button>
            <Button className="rounded-xl font-medium" onClick={saveCurrent} data-testid="confirm-save-filter">Gem filter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-medium">Slet projekt?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på, at du vil slette <span className="font-semibold text-[#1A1919]">{deleteTarget?.name}</span>? Alle tilhørende trin og dokumenter fjernes. Denne handling kan ikke fortrydes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="cancel-delete-project">Annullér</Button>
            <Button className="rounded-xl font-medium bg-destructive text-white hover:bg-destructive/90" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-project">{deleting ? "Sletter…" : "Slet projekt"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
